import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import CustomButton from "../CustomButton";
import DataTable, { Column } from "../DataTable";
import ModalLayout from "../layouts/ModalLayout";
import { AppDispatch, RootState } from "../../store";
import { useBranchCode } from "../../util/hooks/useBranch";
import { fetchStaff } from "../../features/staff/staffSlice";
import { AddStaffForm } from "../../types/staff";
import { formatRoleForDisplay } from "../../util/functions/UniversalFunctions";

interface Props {
  onClose?: () => void;
  title?: string;
  actionLabel?: string;
  onAction?: (selectedStaff: AddStaffForm[]) => void;
  multiple?: boolean;
  defaultSelectedIds?: number[];
}

export default function SelectStaffModal({
  onClose,
  title = "Select Staff",
  actionLabel = "Select",
  onAction,
  multiple = false,
  defaultSelectedIds = [],
}: Props) {
  const dispatch = useDispatch<AppDispatch>();
  const branchCode = useBranchCode();

  const { staffList, loading } = useSelector((state: RootState) => state.staff);

  const [selectedIds, setSelectedIds] = useState<number[]>(defaultSelectedIds);

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, branchCode]);

  const toggleRow = (staffId: number) => {
    if (multiple) {
      setSelectedIds((prev) =>
        prev.includes(staffId)
          ? prev.filter((id) => id !== staffId)
          : [...prev, staffId],
      );
    } else {
      setSelectedIds((prev) => (prev.includes(staffId) ? [] : [staffId]));
    }
  };

  const isAllSelected =
    multiple && staffList.length > 0 && selectedIds.length === staffList.length;

  const toggleSelectAll = () => {
    if (!multiple) return;

    setSelectedIds(isAllSelected ? [] : staffList.map((s) => s.staffId));
  };

  const columns: Column<AddStaffForm>[] = [
    {
      label: multiple ? (
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={toggleSelectAll}
        />
      ) : (
        ""
      ),
      accessor: "select",
      render: (item) => (
        <input
          type="checkbox"
          checked={selectedIds.includes(item.staffId)}
          onChange={() => toggleRow(item.staffId)}
        />
      ),
    },
    {
      label: "SL NO.",
      accessor: "staffId",
      render: (_, index) => <p>{index + 1}</p>,
    },
    {
      label: "Profile",
      accessor: "fullName",
      render: (item) => (
        <div>
          {!item?.profile_image_url ? (
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary-600 text-white font-semibold">
              {item.fullName.charAt(0).toUpperCase()}
            </div>
          ) : (
            <img
              src={item?.profile_image_url}
              alt="profile"
              className="w-10 h-10 rounded-full object-cover bg-background-200"
            />
          )}
        </div>
      ),
    },
    {
      label: "Staff Name",
      accessor: "fullName",
      render: (item) => (
        <div className="font-bold text-sm text-neutral-400">
          {item.fullName}
        </div>
      ),
    },
    {
      label: "Designation",
      accessor: "roleName",
      render: (item) => (
        <div className="text-xs text-neutral-300">
          {formatRoleForDisplay(item.roleName) || "----"}
        </div>
      ),
    },
  ];

  const handleSubmit = () => {
    const selectedStaff = staffList.filter((staff) =>
      selectedIds.includes(staff.staffId),
    );

    onAction?.(selectedStaff);
    onClose?.();
  };

  return (
    <ModalLayout onClose={onClose} width="max-w-2xl">
      <div className="flex flex-col gap-7 md:max-h-96 overflow-y-auto custom-scrollbar">
        <span className="font-semibold text-xl text-neutral-400">{title}</span>

        <DataTable columns={columns} data={staffList} isLoading={loading} />

        <div className="flex gap-4 justify-end">
          <CustomButton
            text="Cancel"
            color="bg-neutral-200"
            onClick={onClose}
          />
          <CustomButton
            text={actionLabel}
            color="bg-primary-500"
            disabled={selectedIds.length === 0}
            onClick={handleSubmit}
          />
        </div>
      </div>
    </ModalLayout>
  );
}
